/**
 * LocalForage Storage Abstraction
 *
 * Provides a type-safe wrapper around LocalForage for IndexedDB/LocalStorage
 */

import localforage from 'localforage';

/**
 * Storage instance configuration
 */
interface StorageConfig {
  name: string;
  storeName: string;
  description?: string;
}

/**
 * Storage error types
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly originalError?: Error
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

/**
 * Create a configured LocalForage instance
 */
export function createStorageInstance(config: StorageConfig): LocalForage {
  return localforage.createInstance({
    name: config.name,
    storeName: config.storeName,
    description: config.description,
    driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  });
}

/**
 * Storage instances for different data types
 */
export const dataStore = createStorageInstance({
  name: 'echoes-industry-calc',
  storeName: 'api_data',
  description: 'Cached API data (blueprints, items, skills, etc.)',
});

export const imageStore = createStorageInstance({
  name: 'echoes-industry-calc',
  storeName: 'images',
  description: 'Cached item/blueprint icons',
});

export const settingsStore = createStorageInstance({
  name: 'echoes-industry-calc',
  storeName: 'settings',
  description: 'User settings and preferences',
});

export const metadataStore = createStorageInstance({
  name: 'echoes-industry-calc',
  storeName: 'metadata',
  description: 'Cache metadata (timestamps, versions, etc.)',
});

/**
 * Generic storage operations with error handling
 */
export class Storage<T = unknown> {
  constructor(private store: LocalForage) {}

  /**
   * Get an item from storage
   */
  async get<K = T>(key: string): Promise<K | null> {
    try {
      const value = await this.store.getItem(key);
      return value as K | null;
    } catch (error) {
      throw new StorageError(`Failed to get item: ${key}`, 'GET_ERROR', error as Error);
    }
  }

  /**
   * Set an item in storage
   */
  async set(key: string, value: T): Promise<void> {
    try {
      await this.store.setItem(key, value);
    } catch (error) {
      throw new StorageError(`Failed to set item: ${key}`, 'SET_ERROR', error as Error);
    }
  }

  /**
   * Remove an item from storage
   */
  async remove(key: string): Promise<void> {
    try {
      await this.store.removeItem(key);
    } catch (error) {
      throw new StorageError(`Failed to remove item: ${key}`, 'REMOVE_ERROR', error as Error);
    }
  }

  /**
   * Clear all items from storage
   */
  async clear(): Promise<void> {
    try {
      await this.store.clear();
    } catch (error) {
      throw new StorageError('Failed to clear storage', 'CLEAR_ERROR', error as Error);
    }
  }

  /**
   * Get all keys in storage
   */
  async keys(): Promise<string[]> {
    try {
      return await this.store.keys();
    } catch (error) {
      throw new StorageError('Failed to get storage keys', 'KEYS_ERROR', error as Error);
    }
  }

  /**
   * Get the number of items in storage
   */
  async length(): Promise<number> {
    try {
      return await this.store.length();
    } catch (error) {
      throw new StorageError('Failed to get storage length', 'LENGTH_ERROR', error as Error);
    }
  }

  /**
   * Iterate over all items in storage
   */
  async iterate(callback: (value: T, key: string, index: number) => void): Promise<void> {
    try {
      await this.store.iterate((value, key, index) => {
        callback(value as T, key, index);
      });
    } catch (error) {
      throw new StorageError('Failed to iterate storage', 'ITERATE_ERROR', error as Error);
    }
  }

  /**
   * Check if a key exists in storage
   */
  async has(key: string): Promise<boolean> {
    try {
      const value = await this.store.getItem(key);
      return value !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get multiple items by keys
   */
  async getMany(keys: string[]): Promise<(T | null)[]> {
    try {
      return Promise.all(keys.map((key) => this.get(key)));
    } catch (error) {
      throw new StorageError('Failed to get multiple items', 'GET_MANY_ERROR', error as Error);
    }
  }

  /**
   * Set multiple items at once
   */
  async setMany(items: Record<string, T>): Promise<void> {
    try {
      await Promise.all(Object.entries(items).map(([key, value]) => this.set(key, value)));
    } catch (error) {
      throw new StorageError('Failed to set multiple items', 'SET_MANY_ERROR', error as Error);
    }
  }

  /**
   * Remove multiple items by keys
   */
  async removeMany(keys: string[]): Promise<void> {
    try {
      await Promise.all(keys.map((key) => this.remove(key)));
    } catch (error) {
      throw new StorageError(
        'Failed to remove multiple items',
        'REMOVE_MANY_ERROR',
        error as Error
      );
    }
  }
}

/**
 * Storage instances for different data types
 */
export const dataStorage = new Storage(dataStore);
export const imageStorage = new Storage(imageStore);
export const settingsStorage = new Storage(settingsStore);
export const metadataStorage = new Storage(metadataStore);

/**
 * Clear all application storage
 */
export async function clearAllStorage(): Promise<void> {
  await Promise.all([
    dataStorage.clear(),
    imageStorage.clear(),
    settingsStorage.clear(),
    metadataStorage.clear(),
  ]);
}

/**
 * Get storage usage statistics
 */
export async function getStorageStats() {
  const [dataCount, imageCount, settingsCount, metadataCount] = await Promise.all([
    dataStorage.length(),
    imageStorage.length(),
    settingsStorage.length(),
    metadataStorage.length(),
  ]);

  return {
    data: dataCount,
    images: imageCount,
    settings: settingsCount,
    metadata: metadataCount,
    total: dataCount + imageCount + settingsCount + metadataCount,
  };
}
